// @generated-by docnow-forgot-password-modal v1
// ============================================================
// FORGOT PASSWORD MODAL
// ============================================================
// 2-step flow on the login screen:
//   Step 1: enter email -> POST /auth/request-password-reset -> code emailed
//   Step 2: enter code + new password -> POST /auth/reset-password -> done
// Renders nothing when open is false.
// Backend treats unknown emails as success (no enumeration leak).
// ============================================================
import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';

export default function ForgotPasswordModal({ open, onClose, initialEmail = '' }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setEmail(initialEmail || '');
      setCode('');
      setNewPassword('');
      setConfirmPassword('');
      setShowNew(false);
      setShowConfirm(false);
      setError('');
      setSuccess(false);
      setSubmitting(false);
    }
  }, [open, initialEmail]);

  if (!open) return null;

  const meetsComplexity = (pwd) =>
    pwd.length >= 8 && /[a-z]/.test(pwd) && /[A-Z]/.test(pwd) && /\d/.test(pwd);

  const canSubmitStep1 = email.length > 3 && email.includes('@') && !submitting;
  const canSubmitStep2 =
    code.length === 6 &&
    /^\d{6}$/.test(code) &&
    meetsComplexity(newPassword) &&
    newPassword === confirmPassword &&
    !submitting;

  const handleStep1 = async (e) => {
    e.preventDefault();
    setError('');
    if (!canSubmitStep1) return;
    setSubmitting(true);
    try {
      await authAPI.requestPasswordReset(email.trim().toLowerCase());
      // Backend always returns success to avoid email enumeration.
      // Move to step 2 regardless.
      setStep(2);
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.error) || 'Αποτυχία αποστολής κωδικού';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStep2 = async (e) => {
    e.preventDefault();
    setError('');
    if (!canSubmitStep2) return;
    setSubmitting(true);
    try {
      await authAPI.resetPassword(email.trim().toLowerCase(), code, newPassword);
      setSuccess(true);
      setTimeout(() => onClose && onClose(), 2500);
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.error) || 'Λάθος ή ληγμένος κωδικός';
      setError(msg);
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: 10,
    borderRadius: 8, border: '1px solid #E8ECF2',
    fontSize: 14, fontFamily: 'inherit',
  };
  const inputStylePwd = { ...inputStyle, paddingRight: 44 };
  const eyeBtnStyle = {
    position: 'absolute', right: 12, top: '50%',
    transform: 'translateY(-50%)',
    background: 'none', border: 'none',
    fontSize: 18, cursor: 'pointer', color: '#8A97A8',
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose && onClose(); }}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15, 31, 61, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2000,
      }}
    >
      <div style={{
        background: 'white', borderRadius: 12,
        padding: 28, maxWidth: 480, width: '90%',
        boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        position: 'relative',
      }}>
        <button
          onClick={() => !submitting && onClose && onClose()}
          aria-label="Close"
          style={{
            position: 'absolute', right: 12, top: 12,
            background: 'none', border: 'none',
            fontSize: 22, cursor: 'pointer', color: '#8A97A8',
          }}
        >×</button>

        <h2 style={{ margin: '0 0 6px 0', fontSize: 20, color: '#0F1F3D' }}>
          Επαναφορά Κωδικού
        </h2>

        {success ? (
          <div style={{ marginTop: 18 }}>
            <div style={{
              padding: 16, borderRadius: 8,
              background: '#D1FAE5', color: '#065F46',
              fontWeight: 600, textAlign: 'center',
            }}>
              Ο κωδικός ενημερώθηκε επιτυχώς
            </div>
            <p style={{ marginTop: 14, fontSize: 13, color: '#8A97A8', textAlign: 'center' }}>
              Μπορείτε να συνδεθείτε με τον νέο κωδικό.
            </p>
          </div>
        ) : step === 1 ? (
          <>
            <p style={{ margin: '0 0 20px 0', fontSize: 13, color: '#8A97A8' }}>
              Εισάγετε το email σας και θα σας στείλουμε κωδικό επαναφοράς.
            </p>
            <form onSubmit={handleStep1}>
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#0F1F3D' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  disabled={submitting}
                  style={inputStyle}
                  placeholder="admin@docnow.gr"
                />
              </div>

              {error && (
                <div style={{
                  marginBottom: 16, padding: 10, borderRadius: 6,
                  background: '#FEE2E2', color: '#991B1B', fontSize: 13,
                }}>{error}</div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => !submitting && onClose && onClose()}
                  disabled={submitting}
                  style={{
                    background: 'transparent', color: '#475569',
                    border: '1px solid #E8ECF2', borderRadius: 8,
                    padding: '10px 18px', fontWeight: 600,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                  }}
                >Ακύρωση</button>
                <button
                  type="submit"
                  disabled={!canSubmitStep1}
                  style={{
                    background: canSubmitStep1 ? '#C0185A' : '#D1D5DB',
                    color: 'white', border: 'none', borderRadius: 8,
                    padding: '10px 18px', fontWeight: 600,
                    cursor: canSubmitStep1 ? 'pointer' : 'not-allowed',
                  }}
                >{submitting ? 'Αποστολή...' : 'Αποστολή Κωδικού'}</button>
              </div>
            </form>
          </>
        ) : (
          <>
            <p style={{ margin: '0 0 20px 0', fontSize: 13, color: '#8A97A8' }}>
              Αν το email υπάρχει, στείλαμε κωδικό 6 ψηφίων στο <strong>{email}</strong>. Ο κωδικός ισχύει για 10 λεπτά.
            </p>
            <form onSubmit={handleStep2}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#0F1F3D' }}>
                  Κωδικός από email (6 ψηφία)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoFocus
                  disabled={submitting}
                  style={{ ...inputStyle, letterSpacing: 4, textAlign: 'center', fontSize: 18, fontWeight: 600 }}
                  placeholder="000000"
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#0F1F3D' }}>
                  Νέος κωδικός
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={submitting}
                    style={inputStylePwd}
                    placeholder="τουλάχιστον 8 χαρακτήρες"
                  />
                  <button type="button" onClick={() => setShowNew((s) => !s)} style={eyeBtnStyle}>
                    {showNew ? '🙈' : '👁'}
                  </button>
                </div>
                {newPassword && !meetsComplexity(newPassword) && (
                  <div style={{ marginTop: 6, fontSize: 11, color: '#92400E' }}>
                    Τουλάχιστον 8 χαρακτήρες, κεφαλαία, πεζά και αριθμό
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#0F1F3D' }}>
                  Επιβεβαίωση νέου κωδικού
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={submitting}
                    style={inputStylePwd}
                    placeholder="επαναλάβετε τον νέο κωδικό"
                  />
                  <button type="button" onClick={() => setShowConfirm((s) => !s)} style={eyeBtnStyle}>
                    {showConfirm ? '🙈' : '👁'}
                  </button>
                </div>
                {confirmPassword && newPassword !== confirmPassword && (
                  <div style={{ marginTop: 6, fontSize: 11, color: '#991B1B' }}>
                    Δεν ταιριάζει
                  </div>
                )}
              </div>

              {error && (
                <div style={{
                  marginBottom: 16, padding: 10, borderRadius: 6,
                  background: '#FEE2E2', color: '#991B1B', fontSize: 13,
                }}>{error}</div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => !submitting && setStep(1)}
                  disabled={submitting}
                  style={{
                    background: 'transparent', color: '#C0185A',
                    border: 'none', padding: '10px 4px',
                    fontWeight: 600, fontSize: 13,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                  }}
                >← Αλλαγή email</button>
                <button
                  type="submit"
                  disabled={!canSubmitStep2}
                  style={{
                    background: canSubmitStep2 ? '#C0185A' : '#D1D5DB',
                    color: 'white', border: 'none', borderRadius: 8,
                    padding: '10px 18px', fontWeight: 600,
                    cursor: canSubmitStep2 ? 'pointer' : 'not-allowed',
                  }}
                >{submitting ? 'Επαναφορά...' : 'Επαναφορά Κωδικού'}</button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
